import dayjs from 'dayjs';

import type {
  SignatureV2ParticipantInput,
  SignatureV2Workflow,
  SignatureV2WorkflowStatus,
} from '@/types/signatureV2';

const pollingStatuses: ReadonlySet<SignatureV2WorkflowStatus> = new Set<SignatureV2WorkflowStatus>([
  'configuring',
  'ready',
  'preparing',
  'in_progress',
  'awaiting_completion',
  'finalizing',
  'cancelling',
  'terminating',
]);

export type SignatureV2RosterValidation = 'empty' | 'too_many' | 'missing_fields' | 'invalid_email' | 'duplicate_email';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const shouldPollSignatureV2Workflow = (status: SignatureV2WorkflowStatus): boolean =>
  pollingStatuses.has(status);

export const shouldAutoActivateSignatureV2Workflow = (
  workflow: Pick<SignatureV2Workflow, 'status' | 'available_actions'>
): boolean => workflow.status === 'ready' && workflow.available_actions.includes('activate');

export const getSignatureV2RosterConfirmationMessageKey = (status: SignatureV2WorkflowStatus): string => {
  if (status === 'configuring') return 'signatureV2.contract.readySaved';
  if (status === 'ready' || status === 'preparing') return 'signatureV2.contract.workflowPreparing';
  if (status === 'in_progress') return 'signatureV2.contract.firstSignerReady';
  return 'signatureV2.contract.rosterSaved';
};

export const getSignatureV2WorkflowNoticeKey = (status: SignatureV2WorkflowStatus): string | null => {
  if (status === 'ready') return 'signatureV2.contract.autoPreparing';
  if (status === 'preparing') return 'signatureV2.contract.workflowPreparingAutomatically';
  if (status === 'awaiting_completion') return 'signatureV2.contract.awaitingCompletion';
  return null;
};

export const formatSignatureV2SignedDate = (signedAt?: string | null): string => {
  if (!signedAt) return '-';
  const value = dayjs(signedAt);
  return value.isValid() ? value.format('YYYY/MM/DD') : '-';
};

export const validateSignatureV2Roster = (
  signers: readonly SignatureV2ParticipantInput[],
  otherPartyEmails: readonly string[] = []
): SignatureV2RosterValidation | null => {
  if (signers.length === 0) return 'empty';
  if (signers.length + otherPartyEmails.length > 10) return 'too_many';

  const seen = new Set(otherPartyEmails.map((email) => email.trim().toLowerCase()).filter(Boolean));
  for (const signer of signers) {
    const name = signer.name.trim();
    const email = signer.email.trim().toLowerCase();
    if (!name || !email) return 'missing_fields';
    if (!emailPattern.test(email)) return 'invalid_email';
    if (seen.has(email)) return 'duplicate_email';
    seen.add(email);
  }

  return null;
};

export const getSignatureV2ApiError = (error: unknown): { code?: string; message?: string } => {
  const response = (error as { response?: { data?: { message?: string; data?: { code?: string } } } }).response;
  return { code: response?.data?.data?.code, message: response?.data?.message };
};
